"use client";

import { ReactNode, useEffect, useState } from "react";
import { useConvexAuth } from "convex/react";
import { useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import { REFERRAL_CODE_SESSION_KEY } from "@/lib/constants";

interface UserInitializerProps {
  children: ReactNode;
}

/**
 * UserInitializer Component
 *
 * Ensures that users are automatically created in the Convex database
 * when they first authenticate via Clerk. This component:
 *
 * 1. Detects when a user is authenticated
 * 2. Calls the getOrCreateUser mutation
 * 3. Shows a loading state during user creation
 * 4. Handles errors gracefully
 * 5. Only renders children once the user is fully initialized
 *
 * This prevents the "User not found in database" error that would
 * otherwise occur when authenticated users try to access protected routes.
 */
export function UserInitializer({ children }: UserInitializerProps) {
  // NOTE: This component wraps {children} INSIDE ConvexClientProvider but ABOVE
  // LocaleProvider/NextIntlClientProvider (see app/layout.tsx + ConvexClientProvider),
  // so the i18n context is NOT in scope here. It CANNOT use useTranslations — keep
  // these gate/loading strings hardcoded (same rationale as OfflineIndicator and
  // app/global-error.tsx).
  const { isAuthenticated, isLoading } = useConvexAuth();
  const getOrCreateUser = useMutation(api.auth.getOrCreateUser);
  const [userReady, setUserReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(false);

  useEffect(() => {
    // Only initialize if authenticated and not already initialized
    if (isAuthenticated && !userReady && !isInitializing && !isLoading) {
      setIsInitializing(true);
      setError(null);

      // Read referral code from sessionStorage (set by signup page)
      let referralCode: string | undefined;
      try {
        referralCode = sessionStorage.getItem(REFERRAL_CODE_SESSION_KEY) || undefined;
      } catch {
        // sessionStorage may throw in private browsing or when storage is full
      }

      getOrCreateUser({ referralCode })
        .then(() => {
          console.log("✓ User initialized successfully");
          try { sessionStorage.removeItem(REFERRAL_CODE_SESSION_KEY); } catch {}
          setUserReady(true);
          setIsInitializing(false);
        })
        .catch((err) => {
          console.error("Failed to initialize user:", err);
          setError(
            err.message || "Failed to initialize user. Please try refreshing the page."
          );
          setIsInitializing(false);
        });
    }
  }, [isAuthenticated, userReady, isInitializing, isLoading, getOrCreateUser]);

  // Reset userReady state when user logs out
  useEffect(() => {
    if (!isAuthenticated && !isLoading) {
      setUserReady(false);
      setError(null);
    }
  }, [isAuthenticated, isLoading]);

  // Show loading state while Convex auth is initializing
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show loading state while initializing user
  if (isAuthenticated && !userReady && !error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Setting up your account...</p>
        </div>
      </div>
    );
  }

  // Show error state with retry option
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-md p-6 bg-red-50 rounded-lg">
          <svg
            className="h-12 w-12 text-red-600 mx-auto mb-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Initialization Error
          </h2>
          <p className="text-gray-700 mb-4">{error}</p>
          <button
            onClick={() => {
              setError(null);
              setUserReady(false);
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // User is either not authenticated (will show public pages) or is ready
  return <>{children}</>;
}
